// Ghost-click guard for the AssetCounter +/- buttons.
//
// A press-and-hold fires an auto-tick, then the browser dispatches the
// touch-compatibility "ghost" click when the finger lifts. That ghost click
// must NOT land as a second count on top of the hold tick (the field "asset
// quantities are double counting" report). Suppression here is:
//
//   - TIMING-INDEPENDENT: the ghost click is identified by IDENTITY, not by a
//     wall-clock window, so a click delayed arbitrarily (a busy main thread
//     under backed-up sync) is still caught. An earlier fix used a 250→700 ms
//     window; a click delayed past it slipped through as a double.
//   - ENQUEUED AT HOLD-FIRE, not at release. The instant a hold fires we KNOW
//     its release will spawn exactly one trailing compat click, so we owe one
//     suppression *now*. Nothing that happens between hold-fire and the click —
//     a `pointerleave` from finger drift, a second press landing before release
//     (Codex P1), a second finger during a count-down (the multi-touch
//     remove-at-zero case) — can drop the owed ghost. An earlier version owed
//     the ghost only at `pointerup` and cleared a marker on `pointerleave` /
//     the next `pointerdown`, which re-opened the double-count when the pointer
//     drifted off and back, or a second press intervened.
//   - PER-DIRECTION: `+` and `-` keep separate counters, so a `+`-hold ghost
//     can never swallow a genuine `-` tap (and vice-versa).
//   - KEYBOARD-SAFE: a keyboard activation fires a click with `detail === 0`,
//     which can never be a touch-compat ghost, so it always runs.
//
// The only event that means "no compat click will follow" is `pointercancel`
// (the browser took the pointer for a scroll/pan) — that, and only that, gives
// the owed ghost back (`noteCancel`). A `pointerleave` is NOT terminal (the
// finger can re-enter and release with a click), so it does nothing here.
//
// Residual: if a MOUSE holds, drifts off, and releases genuinely outside the
// button (no compat click, and no `pointercancel`), the owed ghost is never
// consumed and the NEXT genuine tap is swallowed once — a rare, self-healing
// UNDER-count, strictly preferable to a contractual OVER-count. Touch drift
// fires `pointercancel`, so touch has no such residual.
//
// The guard is a plain closure over mutable counters (held in the component via
// a stable ref); no React, no DOM — so every interleaving is unit-testable
// directly in asset-counter-ghost-guard.test.ts.

export type CounterDirection = 'increment' | 'decrement'

export interface GhostClickGuard {
  /** Any primary (button 0) pointerdown — starts a fresh press. */
  beginPress(): void
  /** A hold auto-tick fired during the current press (owes one ghost now). */
  noteHoldFired(direction: CounterDirection): void
  /** Pointerup ON the button — its compat click is coming to consume the ghost. */
  noteRelease(): void
  /** Pointercancel — the browser took the pointer, so NO click follows; give the ghost back. */
  noteCancel(): void
  /**
   * Decide whether a click should run its handler. Consumes one owed ghost for
   * `direction` when applicable. `detail` is the MouseEvent click count
   * (0 = keyboard, >= 1 = pointer/compat).
   */
  shouldRunClick(direction: CounterDirection, detail: number): boolean
}

export function createGhostClickGuard(): GhostClickGuard {
  // Count of trailing "ghost" clicks still owed per direction.
  const pending: Record<CounterDirection, number> = {
    increment: 0,
    decrement: 0,
  }
  // The direction THIS press owes a ghost for (null if none). Enables
  // `noteCancel` to return the owed ghost, and prevents a long multi-tick hold
  // from owing more than one. Reset per press by `beginPress` so a previous
  // press's stale marker (e.g. a mouse released off-button) can't block the
  // next press from owing its own ghost.
  let pressGhost: CounterDirection | null = null

  return {
    beginPress() {
      // Fresh press. Does NOT touch `pending` — a prior press's still-owed ghost
      // must survive an overlapping/second press.
      pressGhost = null
    },
    noteHoldFired(direction) {
      if (pressGhost === null) {
        pending[direction] += 1
        pressGhost = direction
      }
    },
    noteRelease() {
      // The finger lifted on the button; its compat click will consume the owed
      // ghost. Close the press without touching `pending`.
      pressGhost = null
    },
    noteCancel() {
      // No compat click will follow — return the ghost this press owed.
      if (pressGhost !== null) {
        pending[pressGhost] -= 1
        pressGhost = null
      }
    },
    shouldRunClick(direction, detail) {
      if (detail === 0) return true // keyboard is never a ghost
      if (pending[direction] > 0) {
        pending[direction] -= 1
        return false
      }
      return true
    },
  }
}
